// Live view of whether THIS device (browser or Capacitor native shell) will
// actually receive push notifications. `usePushRegistration` owns the token
// lifecycle; this hook is read-only status the settings screen renders so the
// operator can confirm at a glance that alerts are - or aren't - switched on.
//
// One code path for all platforms: `FirebaseMessaging.checkPermissions()`
// proxies to the browser Notification permission on web and to the native
// permission on iOS/Android, so the reported state matches what the OS/browser
// would deliver.

import { useCallback, useEffect, useState } from 'react';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import {
  currentPlatform,
  isPushConfigured,
  isPushServiceBlocked,
  registerPush,
  type PushPlatform,
} from './usePushRegistration';

// 'checking'      - first read in flight
// 'unconfigured'  - this build has no Firebase credentials; no push transport
// 'unsupported'   - platform can't receive push (e.g. browser without Notification API)
// 'prompt'        - permission not decided yet; the user can enable it
// 'granted'       - alerts will be delivered
// 'denied'        - the user blocked alerts; must be re-enabled in OS/browser settings
export type PushPermissionStatus =
  | 'checking'
  | 'unconfigured'
  | 'unsupported'
  | 'prompt'
  | 'granted'
  | 'denied'
  | 'browserBlocked';

export interface PushPermission {
  status: PushPermissionStatus;
  platform: PushPlatform;
  /** True while an enable request is in flight. */
  enabling: boolean;
  /** Re-read the current permission from the OS/browser. */
  refresh: () => Promise<void>;
  /** Trigger the permission prompt + token registration, then re-read. */
  enable: () => Promise<void>;
}

const webNotificationSupported = (): boolean =>
  typeof window !== 'undefined' && 'Notification' in window;

export function usePushPermission(): PushPermission {
  const platform = currentPlatform();
  const [status, setStatus] = useState<PushPermissionStatus>('checking');
  const [enabling, setEnabling] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushConfigured) {
      setStatus('unconfigured');
      return;
    }
    if (platform === 'web' && !webNotificationSupported()) {
      setStatus('unsupported');
      return;
    }
    try {
      const { receive } = await FirebaseMessaging.checkPermissions();
      if (receive === 'granted' && isPushServiceBlocked()) {
        setStatus('browserBlocked');
        return;
      }
      // The plugin distinguishes 'prompt' and 'prompt-with-rationale'; both mean
      // "not decided, can ask".
      setStatus(receive === 'granted' || receive === 'denied' ? receive : 'prompt');
    } catch {
      setStatus('unsupported');
    }
  }, [platform]);

  const enable = useCallback(async () => {
    setEnabling(true);
    try {
      await registerPush();
    } finally {
      setEnabling(false);
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
    // Re-read when the tab regains focus - the user may have flipped the
    // permission in OS/browser settings while we were backgrounded.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  return { status, platform, enabling, refresh, enable };
}
